// Fires one page_view event on every route change, using the taxonomy
// resolver. Ignores:
//   1. Identical successive paths — React Router can fire twice during
//      navigation reconciliation; React 18 StrictMode fires effects twice
//      in dev. Same-key emits collapse to one via `lastKeyRef`.
//   2. The bare root `/` — after login the app redirects `/` →
//      `/workspace/queue` (or role default) and both fire. Emitting on `/`
//      inflates a meaningless `Other → unknown` bucket. Skip it.

import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuthStore } from '@/shared/stores/auth.store'
import { normalisePath } from './path-normalise'
import { fallbackResolution, resolveRoute } from './route-map'
import { track } from './track'

export function usePageView(): void {
  const location = useLocation()
  // Subscribe so the effect re-runs when auth bootstraps. On a hard reload
  // of a protected page, `user` is null for the first ~300 ms while
  // useAuthInit calls silent-refresh + /auth/me; `track` would silently
  // drop the landing page-view. Retriggering when the user materialises
  // fires it exactly once, guarded by lastKeyRef.
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const lastKeyRef = useRef<string | null>(null)

  useEffect(() => {
    // Skip bare root — redirect landing; the destination page will emit next.
    if (location.pathname === '/') return
    // Pre-auth pages (login, reset-password) are noise in product analytics —
    // they exist to gate access, not measure feature usage. Skip the whole
    // /auth/* namespace.
    if (location.pathname.startsWith('/auth')) return
    // Hold off until auth resolves — otherwise track() drops the event
    // (role gate returns false when user is null).
    if (!isAuthenticated) return

    const key = `${location.pathname}${location.search}`
    if (lastKeyRef.current === key) return
    lastKeyRef.current = key

    const path = normalisePath(location.pathname, location.search)
    const resolution = resolveRoute(path) ?? fallbackResolution(path)
    track(resolution.area, resolution.feature, resolution.subFeature)
  }, [location.pathname, location.search, isAuthenticated])
}
